import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FeriasForm, FeriasSchema } from "../../../schemas/Funcionarios_cadastro/ferias_schema";
import { useCallback, useEffect } from "react";
import { Ferias } from "../../../../domain/models/ferias";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * CadastroFeriasModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedFerias: Ferias | null,
  onSubmit: (form: FeriasForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const CadastroFeriasModal = ({
  open,
  saving,
  selectedFerias,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<FeriasForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(FeriasSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedFerias) {
        hookForm.setValue('dataInicio', selectedFerias.dataInicio);
        hookForm.setValue('dataTermino', selectedFerias.dataTermino);
        hookForm.setValue('descricao', selectedFerias.descricao);
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedFerias,
  ]);

  const handleSubmit = useCallback((form: FeriasForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedFerias) {
      onOpenChangeHistory(selectedFerias.idFerias, LogTipoTabela.FUNCIONARIO_FERIAS);
    }
  }, [selectedFerias, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Férias
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name='descricao'
                label='Descrição'
                readOnly={saving}
                multiline={true}
                rows={3}
                reactHookForm={hookForm}
              />

              <Controller
                name='dataInicio'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                    label="Data de Início"
                    value={field.value ? dayjs.utc(field.value as any).local() : null}
                    onChange={e => field.onChange(e !== null ? e.startOf('day').utc().toDate() : null)} />
                )} />

              <Controller
                name='dataTermino'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                    label="Data de Término"
                    value={field.value ? dayjs.utc(field.value as any).local() : null}
                    onChange={e => field.onChange(e !== null ? e.startOf('day').utc().toDate() : null)} />
                )} />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedFerias) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}